import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import {
  WorkbenchConfiguration,
  setNumberOfAssembly,
} from 'src/app/models/configurators-2d/applications/workbench/configurations/configuration';
import { AdministratorsService } from '../administrations/administrators-service';
import { BaseAppService } from '../base-app-service';
import { Material } from '../../../models/configurators-2d/applications/workbench/materials/material';
import { Shape } from '../../../models/configurators-2d/applications/workbench/shape';
import { PlanRestriction } from 'src/app/models/configurators-2d/applications/workbench/constraints/plan-restriction';
import { ConfigurationPlan } from 'src/app/models/configurators-2d/applications/workbench/configurations/configuration-plan';
import { ConfigurationGroup } from 'src/app/models/configurators-2d/applications/workbench/configurations/configuration-group';
import { Cutaway } from 'src/app/models/configurators-2d/applications/workbench/accessories/cutaway';
import { Resume } from 'src/app/models/configurators-2d/applications/workbench/resume';
import { Cutting } from 'src/app/models/configurators-2d/applications/workbench/cutting';
import { SideCut } from 'src/app/models/configurators-2d/applications/workbench/side-cut';
import { Quotation } from 'src/app/models/configurators-2d/quotation/quotation';
import { normalizePlanEdgeCollections } from 'src/app/pages/configurators-2d/workbench/preview/services/workbench-edge-utils';

@Injectable({
  providedIn: 'root',
})
export class WorkbenchService extends BaseAppService<WorkbenchConfiguration> {
  constructor(protected administratorsService: AdministratorsService) {
    super(CONFIG_CONTEXT.WORKBENCH, WorkbenchConfiguration);
  }

  override saveProject(): Promise<boolean> {
    this.normalizeEdgeCollections();
    this.configuration.numberOfAssembly = setNumberOfAssembly(this.configuration.workbenchConfigurationGroups);
    return super.saveProject();
  }

  override getResume<R extends Resume>(): Promise<R> {
    this.normalizeEdgeCollections();
    this.configuration.numberOfAssembly = setNumberOfAssembly(this.configuration.workbenchConfigurationGroups);
    return super.getResume<R>(this.configuration);
  }

  override async getProjectById(id: string, setConfig: boolean = false): Promise<WorkbenchConfiguration> {
    const configuration = await super.getProjectById(id, setConfig);
    this.normalizeEdgeCollections(configuration);
    return configuration;
  }

  /**
   * Fonction de récupération des matériaux
   * @returns Promise
   */
  getShapes(): Promise<Shape[]> {
    return this.get(this.apiUrl + 'Configuration/Shapes');
  }

  /**
   * Fonction de récupération des matériaux, épaisseurs et coloris
   * @returns Promise
   */
  async getMaterialsColors(shapeIds: number[]): Promise<Material[]> {
    try {
      const params = new HttpParams({
        fromObject: { shapeIds: shapeIds },
      });

      const res: any[] = await this.get<Material[]>(this.apiUrl + 'Configuration/MaterialsColors', params);
      for (const m of res) {
        m.selectable = true;
      }
      return res;
    } catch {
      return [];
    }
  }

  /**
   * Fonction de récupération des restrictions du plan
   * @returns Promise
   */
  getPlanRestrictions(plan: ConfigurationPlan): Promise<PlanRestriction> {
    const params = new HttpParams()
      .set('shapeId', plan.shapeId)
      .set('length', plan.length)
      .set('width', plan.width)
      .set('diameter', plan.diameter)
      .set('shapeType', plan.shape.shapeType);
    return this.get<PlanRestriction>(this.apiUrl + 'Configuration/PlanRestrictions', params);
  }

  /**
   * Récupère les découpe d'angles.
   * @returns
   */
  getCutaways(): Promise<Cutaway[]> {
    return this.get<Cutaway[]>(this.apiUrl + 'Configuration/Cutaways');
  }

  /**
   * Récupère les ouvertures de pkans et de crédences.
   * @returns
   */
  getCuttings(materialId: number): Promise<Cutting[]> {
    const params = new HttpParams().set('materialId', materialId);
    return this.get<Cutting[]>(this.apiUrl + 'Configuration/Cuttings', params);
  }

  /**
   * Récupère les ouvertures de pkans et de crédences.
   * @returns
   */
  getSideCuts(materialId: number): Promise<SideCut[]> {
    const params = new HttpParams().set('materialId', materialId);
    return this.get<SideCut[]>(this.apiUrl + 'Configuration/SideCuts', params);
  }

  /**
   * Fonction de mise à jour de preview
   * @returns Promise
   */
  uploadPdf(pdfString: string): Promise<void> {
    const body = {
      ConfigurationId: this.configuration.id,
      HtmlPdfString: pdfString,
    };

    return this.post(this.apiUrl + 'Data/Pdf', body);
  }

  /**
   * Fonction de mise à jour de preview
   * @returns Promise
   */
  uploadPreview(formFile): Promise<void> {
    const body = {
      ConfigurationId: this.configuration.id,
      FormFile: formFile,
    };

    return this.post(this.apiUrl + 'Data/UpdatePreview', body);
  }

  /**
   * Fonction de récuperation du résumé par ID
   * @remarks
   * Pour le moment uniquement utile lors du chargement d'un projet Workbench en mode tunnel kitchen, car on n'a que l'id du projet workbench et non la config.
   * Je n'ajoute pas cette méthode dans le BaseAppService pour le moment car c'est utile que à workbench, mais si cette méthode est utile à d'autre, il faudra bouger ça dans BaseAppService
   * @returns
   */
  getResumeById<R extends Resume>(configurationId: string): Promise<R> {
    const params = new HttpParams().set('configurationId', configurationId);
    return this.get<R>(`${this.apiUrl}Configuration/Resume`, params);
  }

  getQuotationForKitchen(id: string): Promise<Quotation> {
    const params = new HttpParams().set('id', id);
    return this.get<Quotation>(`${this.apiUrl}Order/Quotation`, params);
  }

  private normalizeEdgeCollections(configuration: WorkbenchConfiguration = this.configuration): void {
    configuration?.workbenchConfigurationPlans?.forEach(plan => normalizePlanEdgeCollections(plan));
  }

  /**
   * Créer ou mettre à jour les groupes de plan de travail à la config.
   */
  createOrUpdateConfigurationGroup(plansIds: number[]): void {
    if (this.configuration.workbenchConfigurationGroups == null) {
      this.configuration.workbenchConfigurationGroups = [];
    }

    // Ensure we are working with valid plans
    const validPlansIds = plansIds.filter(id =>
      this.configuration.workbenchConfigurationPlans.some(p => p.displayOrder === id)
    );

    if (validPlansIds.length < 2) return;

    let targetGroupId: string | null = null;
    let targetGroup: ConfigurationGroup | undefined;

    // Find a candidate group from existing plans in the new group definition
    for (const planId of validPlansIds) {
      const plan = this.configuration.workbenchConfigurationPlans.find(p => p.displayOrder === planId);
      if (plan && plan.isInGroup && plan.groupReferenceId) {
        targetGroupId = plan.groupReferenceId;
        break;
      }
    }

    if (targetGroupId) {
      targetGroup = this.configuration.workbenchConfigurationGroups.find(g => g.id === targetGroupId);
    }

    // If no group found, or group not found provided ID, create new
    if (!targetGroup) {
      const displayOrder = (this.configuration.workbenchConfigurationGroups.length || 0) + 1;
      targetGroup = new ConfigurationGroup(displayOrder);
      this.configuration.workbenchConfigurationGroups.push(targetGroup);
    }

    // Now, force all plans into this group
    validPlansIds.forEach(planId => {
      const plan = this.configuration.workbenchConfigurationPlans.find(p => p.displayOrder === planId);
      if (!plan) return;

      // If plan was in ANOTHER group, remove it from there
      if (plan.isInGroup && plan.groupReferenceId && plan.groupReferenceId !== targetGroup!.id) {
        this.removePlanFromGroup(plan);
      }

      // Add to target group if not already there
      if (plan.groupReferenceId !== targetGroup!.id) {
        plan.isInGroup = true;
        plan.groupReferenceId = targetGroup!.id;

        // Add to group's plan list if not present
        if (!targetGroup!.workbenchConfigurationPlans.some(p => p.displayOrder === plan.displayOrder)) {
          targetGroup!.workbenchConfigurationPlans.push(plan);
        }
      }
    });

    // Cleanup empty groups is now handled implicitly by updates, but let's run a sweep to be sure
    this.cleanupEmptyGroups();
  }

  /**
   * Mettre à jour ou supprimer les groupes de plan de travail à la config.
   */
  updateOrDeleteConfigurationGroup(activePlanId: number): void {
    const plan = this.configuration.workbenchConfigurationPlans.find(x => x.displayOrder == activePlanId);
    if (!plan) return;

    if (plan.isInGroup) {
      this.removePlanFromGroup(plan);
      this.cleanupEmptyGroups();
    }
  }

  private removePlanFromGroup(plan: ConfigurationPlan) {
    if (!plan.groupReferenceId) return;

    const groupIndex = this.configuration.workbenchConfigurationGroups.findIndex(g => g.id === plan.groupReferenceId);
    if (groupIndex !== -1) {
      const group = this.configuration.workbenchConfigurationGroups[groupIndex];
      const planIndex = group.workbenchConfigurationPlans.findIndex(p => p.displayOrder === plan.displayOrder);
      if (planIndex !== -1) {
        group.workbenchConfigurationPlans.splice(planIndex, 1);
      }
    }
    plan.isInGroup = false;
    plan.groupReferenceId = null;
  }

  private cleanupEmptyGroups() {
    const groupsToRemove: string[] = [];

    this.configuration.workbenchConfigurationGroups.forEach(g => {
      if (g.workbenchConfigurationPlans.length < 2) {
        groupsToRemove.push(g.id);
      }
    });

    groupsToRemove.forEach(gid => {
      const index = this.configuration.workbenchConfigurationGroups.findIndex(g => g.id === gid);
      if (index !== -1) {
        const group = this.configuration.workbenchConfigurationGroups[index];
        group.workbenchConfigurationPlans.forEach(p => {
          const configPlan = this.configuration.workbenchConfigurationPlans.find(cp => cp.displayOrder === p.displayOrder);
          if (configPlan) {
            configPlan.isInGroup = false;
            configPlan.groupReferenceId = null;
          }
        });
        this.configuration.workbenchConfigurationGroups.splice(index, 1);
      }
    });
    this.configuration.workbenchConfigurationGroups.forEach((g, i) => g.displayOrder = i + 1);
  }
}
